import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  getWorkspaceId,
  useAppSelector,
  usePriceListMutation,
} from '@zonofi/seller-rtk-store';
import { debounce } from 'lodash';
import { SHARED_SVGS } from '../assets';
import moment from 'moment';

// Types for the dropdown props
interface PriceListOption {
  id: string;
  title: string;
  subtitle: string;
  date: string;
  selected?: boolean;
  priceListId: string;
}

interface selectedOptionProps {
  id: string;
  title: string;
}

interface PriceListDropdownProps {
  onChange: (selectedOption: PriceListOption) => void;
  placeholder?: string;
  className?: string;
  value?: selectedOptionProps | null;
  screen?: 'CUSTOMERS' | 'CFA';
  priceListDisable?: boolean;
}

// Styled Autocomplete to match the original styles
const StyledAutocomplete = styled(
  Autocomplete<PriceListOption, false, true, false>
)({
  width: '100%',
  '& .MuiInputBase-root': {
    padding: '10px 12px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid #d9d9d9',
    backgroundColor: 'white',
    '&:hover': {
      border: '1px solid #000',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    '&.Mui-focused': {
      border: '1px solid #32a7e8',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    '&::placeholder': {
      color: '#bfbfbf',
      opacity: 1,
    },
  },
  '& .MuiAutocomplete-endAdornment': {
    right: '4px',
  },
  '& .MuiAutocomplete-popupIndicator': {
    padding: '0 0.75rem',
    '& img': {
      width: '20px',
      height: '20px',
      transition: 'transform 0.2s ease',
      transform: 'rotate(180deg)',
    },
  },
  '& .MuiAutocomplete-popupIndicatorOpen img': {
    transform: 'rotate(0deg)',
  },
});

// Styled Paper for dropdown options
const StyledPaper = styled('div')({
  marginTop: '0.25rem',
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '0.375rem',
  boxShadow:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#cbd5e0',
    borderRadius: '3px',
  },
});

export const PriceListDropdown: React.FC<PriceListDropdownProps> = ({
  onChange,
  placeholder = 'Select price list...',
  className = '',
  value = null,
  screen,
  priceListDisable = false,
}) => {
  const [options, setOptions] = useState<PriceListOption[]>([]);
  const selectedPrincipal = useAppSelector(getWorkspaceId);
  const [priceListReq, priceListResonse] = usePriceListMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<selectedOptionProps | null>(
      value || options.find((option) => option.selected) || null
    );

  useEffect(() => {
    if (value?.title) {
      setSearchTerm(value.title);
    } else {
      setSearchTerm('');
    }
  }, [value?.title]);

  const debouncedPriceListReq = useMemo(
    () =>
      debounce((searchValue: string) => {
        priceListReq({
          priceListRequest: {
            sellerWorkspaceId: selectedPrincipal,
            workspaceId: selectedPrincipal,
            pageNo: 1,
            pageSize: 20,
            searchKey: searchValue,
            criteria:
              screen === 'CUSTOMERS'
                ? ['customer-price', 'customer-segment']
                : screen === 'CFA'
                  ? ['cfa-price']
                  : [],
            status: 'Active',
          },
        });
      }, 500),
    [priceListReq, selectedPrincipal, screen]
  );

  // const filteredOptions = useMemo(() => {
  //   if (
  //     selectedOption &&
  //     searchTerm.toLowerCase() === selectedOption.title.toLowerCase()
  //   ) {
  //     return options;
  //   }
  //   return options.filter((option) =>
  //     option.title.toLowerCase().includes(searchTerm.toLowerCase())
  //   );
  // }, [options, searchTerm, selectedOption]);
  const restoreSelectedValueIfNeeded = (searchTerm: string) => {
    if (!searchTerm && selectedOption?.title) {
      setSearchTerm(selectedOption.title);
    }
  };
  const handleSelect = (option: PriceListOption | null) => {
    if (option) {
      setSelectedOption({
        title: option.title,
        id: option?.id,
      });
      onChange(option);
      setSearchTerm(option.title);
    }
  };

  useEffect(() => {
    return () => {
      debouncedPriceListReq.cancel();
    };
  }, [debouncedPriceListReq]);

  useEffect(() => {
    if (selectedOption) {
      setSearchTerm(selectedOption.title);
      console.log('priceList value response1', searchTerm);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (isOpen) {
      priceListReq({
        priceListRequest: {
          sellerWorkspaceId: selectedPrincipal,
          workspaceId: selectedPrincipal,
          pageNo: 1,
          pageSize: 20,
          criteria:
            screen === 'CUSTOMERS'
              ? ['customer-price', 'customer-segment']
              : screen === 'CFA'
                ? ['cfa-price']
                : [],
          status: 'Active',
        },
      });
    }
  }, [isOpen, selectedPrincipal, screen]);

  useEffect(() => {
    if (priceListResonse.isSuccess) {
      const response = priceListResonse?.data?.pricing || [];
      const formattedData: PriceListOption[] = response
        ?.filter((option) => option.isActive)
        ?.map((note) => ({
          id: note?.code,
          title: note?.name,
          subtitle: note?.criteria,
          date: note?.createdOn,
          priceListId: note?.priceListId,
        }));
      setOptions(formattedData);
    }
  }, [priceListResonse]);

  return (
    <StyledAutocomplete
      className={className}
      options={options}
      getOptionLabel={(option) => option.title}
      value={options.find((opt) => opt.id === selectedOption?.id)}
      disableClearable
      filterOptions={(options) => options}
      onChange={(_, newValue) => handleSelect(newValue)}
      inputValue={searchTerm}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setSearchTerm(newInputValue);
          debouncedPriceListReq(newInputValue);
        }
      }}
      open={isOpen}
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      openOnFocus
      disabled={priceListDisable}
      loading={priceListResonse.isLoading}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      popupIcon={
        <img src={SHARED_SVGS.SelectArrowUp} width={14} height={14} alt="" />
      }
      PaperComponent={({ children }) => <StyledPaper>{children}</StyledPaper>}
      noOptionsText={
        <div
          style={{
            padding: '0.75rem 1rem',
            color: '#a0aec0',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          No price lists found
        </div>
      }
      loadingText={
        <div
          style={{
            padding: '0.75rem 1rem',
            color: '#a0aec0',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          Loading...
        </div>
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          className="primary-font-regular"
          InputProps={{
            ...params.InputProps,
            className: 'primary-font-regular',
            onBlur: (e) => {
              restoreSelectedValueIfNeeded(e.target.value);
            },
          }}
          sx={{
            '& input': {
              fontFamily: 'inherit',
            },
          }}
          inputProps={{
            ...params.inputProps,
            style: { fontSize: 13 },
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            style: {
              fontSize: 13,
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as any;
        const isSelected =
          selectedOption?.id === option.id ||
          selectedOption?.id === option.priceListId;

        return (
          <li key={option.id} {...otherProps} style={{ padding: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderBottom: '1px solid #f7fafc',
                transition: 'background-color 0.15s ease',
                backgroundColor: isSelected ? '#ebf8ff' : 'transparent',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px',
                  minWidth: '40px',
                  minHeight: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#f5f5f5',
                }}
              >
                <img
                  src={SHARED_SVGS.PriceList}
                  width="24"
                  height="24"
                  alt="price-list-icon"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  className="primary-font-semibold"
                  style={{
                    fontWeight: 500,
                    color: isSelected ? '#3182ce' : '#1a202c',
                    marginBottom: '0.25rem',
                  }}
                >
                  {option.title}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 13,
                    color: '#718096',
                  }}
                >
                  <span
                    style={{ marginRight: '0.25rem' }}
                    className="primary-font-regular"
                  >
                    {option.subtitle}
                  </span>
                  <span
                    style={{
                      margin: '0 0.25rem',
                      fontSize: 13,
                      color: '#718096',
                    }}
                  >
                    •
                  </span>
                  <span
                    style={{ marginLeft: '0.25rem' }}
                    className="primary-font-regular"
                  >
                    {moment(option.date).format('DD/MM/YYYY')}
                  </span>
                </div>
              </div>
            </div>
          </li>
        );
      }}
      sx={{
        '& .MuiAutocomplete-listbox': {
          padding: 0,

          '& .MuiAutocomplete-option': {
            padding: 0,

            /* Mouse hover */
            '&:hover': {
              backgroundColor: '#ebf6fd',
            },

            /* 🔑 Arrow-key navigation */
            '&[data-focus="true"]': {
              backgroundColor: '#ebf6fd',
            },

            /* Selected */
            '&[aria-selected="true"]': {
              backgroundColor: '#ebf8ff',
            },

            /* Selected + arrow keys */
            '&[aria-selected="true"][data-focus="true"]': {
              backgroundColor: '#bee3f8',
            },
          },
        },
      }}
    />
  );
};
